/* global Headers, Element, CustomEvent, EventTarget, msal, fetch, LOYALTY_API, DATA_LAYER, aa */

// https://cameronnokes.com/blog/build-your-own-event-emitter-using-only-native-dom-apis/

import { loginRequest, signUpRequest, config } from './auth.config.js'
import { ErrorLevel, AppError, BeternaError, RestError } from './classes/Errors.js'

console.log('AuthService v0.0')

class EventEmitter {
  constructor () {
    if (EventTarget) {
      this._target = new EventTarget()
    } else {
      this._target = new Element()
    }
  }

  on (eventName, listener) {
    return this._target.addEventListener(eventName, listener)
  }

  once (eventName, listener) {
    return this._target.addEventListener(eventName, listener, { once: true })
  }

  off (eventName, listener) {
    return this._target.removeEventListener(eventName, listener)
  }

  emit (eventName, detail) {
    return this._target.dispatchEvent(new CustomEvent(eventName, { detail, cancelable: true }))
  }
}

// Helper function to call MS Graph API endpoint
// using authorization bearer token scheme_
async function fetchWithTokenAsync (endpoint, accessToken, method, body) {
  if (!accessToken) {
    throw new Error('not authenticated')
  }

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`
  headers.append('Authorization', bearer)
  headers.append('Accept', 'application/json')
  headers.append('Content-Type', 'application/json')

  if (body) {
    body = JSON.stringify(body)
  }
  const response = await fetch(endpoint, { method, headers, body })
  let raw = null
  if (response.ok) {
    raw = await response.json()
  } else {
    throw new RestError('fetchWithTokenAsync', response.status, raw, { url: endpoint, body: body })
  }

  if (raw?.status === 1 && raw?.message === 'User is authenticated, but CRM profile was not yet created.') {
    console.warn(raw.message)
    return raw
  }

  if (raw?.status !== 0) {
    throw new BeternaError('fetchWithTokenAsync', ErrorLevel.FATAL, raw, { url: endpoint, body: body })
  }

  return raw
}

class _AuthService extends EventEmitter {
  constructor () {
    super()

    // eslint-disable-next-line no-unused-vars
    const _idTokenClaims = null
    // eslint-disable-next-line no-unused-vars
    const _accountId = null
    // eslint-disable-next-line no-unused-vars
    const _loginHint = null
    // eslint-disable-next-line no-unused-vars
    const _accessToken = null
    // eslint-disable-next-line no-unused-vars
    const _loyaltyId = -1

    this._loginRequest = loginRequest
    this._signUpRequest = signUpRequest

    this._isFinished = false
    this._initPromise = null

    if (!msal) {
      console.error('MSAL not loaded.')
      this.handleFinishedNoAuth()
      return
    }

    this._initPromise = this.initializeAsync()
  }

  async _ensureInitialized () {
    if (this._initPromise) {
      await this._initPromise
    }
  }

  async initializeAsync () {
    this.msalInstance = await msal.PublicClientApplication.createPublicClientApplication(config)

    this.msalInstance
      .handleRedirectPromise()
      .then((resp) => {
        console.log('AuthService: handleRedirectPromise', resp)
        console.log('AuthService: URL hash:', window.location.hash)
        console.log('AuthService: URL search:', window.location.search)

        if (resp && resp.accessToken && resp.account) {
          console.log('AuthService: ✓ Valid response with accessToken and account')
          this.loadToken(resp)
          // } else if (resp && !resp.accessToken && resp.account)  {
          //  console.error("missing access token. API scope problem?")
          //  this.emit("ended", {})
          this.emit('signedIn')
          DATA_LAYER?.loginAsync({
            email: resp.idTokenClaims?.emails?.[0] || resp.idTokenClaims?.email,
            id: resp.account.homeAccountId,
          })
        } else if (resp && resp.account && !resp.accessToken) {
          console.warn('AuthService: ⚠ Response has account but NO accessToken', {
            account: resp.account,
            scopes: resp.scopes,
            state: resp.state,
          })
          this.handleSilentLoading()
        } else if (resp && !resp.account) {
          console.warn('AuthService: ⚠ Response exists but NO account', resp)
          this.handleSilentLoading()
        } else if (resp === null) {
          console.log('AuthService: ℹ No redirect response (null) - checking for existing session')
          this.handleSilentLoading()
        } else {
          console.warn('AuthService: ⚠ Unexpected response state', resp)
          this.handleSilentLoading()
        }
      })
      .catch((error) => {
        console.error('AuthService: ✗ handleRedirectPromise ERROR:', error)
        console.error('AuthService: Error details:', {
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
          subError: error.subError,
          stack: error.stack,
        })

        if (!error.errorMessage) {
          console.log('AuthService: No error message, finishing without auth')
          this.handleFinishedNoAuth()
        } else if (error.errorMessage.indexOf('AADB2C90091') > -1) {
          console.log('AuthService: User cancelled login (AADB2C90091), attempting silent loading')
          this.handleSilentLoading()
        } else {
          console.error('AuthService: Unhandled error type, finishing without auth')
          this.handleFinishedNoAuth()
        }
      })

    // bfcache restore (Back from B2C login) freezes in-memory state, keeping the loader
    // visible - `pageshow` with `event.persisted === true` detects it. We use
    // `addEventListener` (not `window.onpageshow =`) so the three handlers don't clobber
    // each other; here we only clear stale MSAL cache, UI reset lives in AuthStatus/Notifications.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.clearBrowserStorage()
    })
  }

  async redirectSignup (requestOptions = {}) {
    await this._ensureInitialized()
    const params = { ...this._signUpRequest, ...requestOptions }
    console.log('redirectSignup', params)
    this.msalInstance.loginRedirect(params)
  }

  handleSilentLoading () {
    console.log('AuthService: handleSilentLoading started')

    const urlParams = new URLSearchParams(window.location.search)
    const requestedLoginHint = urlParams.get('loginHint')
    console.log('AuthService: Requested loginHint from URL:', requestedLoginHint)

    if (requestedLoginHint) {
      this._loginRequest.loginHint = requestedLoginHint
      urlParams.delete('loginHint')
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`
      window.history.replaceState({}, document.title, newUrl)
      this.msalInstance.loginRedirect(this._loginRequest)
      return
    }

    const currentAccounts = this.msalInstance.getAllAccounts()
    console.log('AuthService: currentAccounts', currentAccounts)
    console.log('AuthService: Number of accounts:', currentAccounts?.length || 0)

    if (currentAccounts && currentAccounts.length > 0) {
      console.log('AuthService: Found existing account, attempting silent token acquisition')
      this._accountId = currentAccounts[0].homeAccountId
      this._loginHint = currentAccounts[0].loginHint
      this._loginRequest.account = this.msalInstance.getAccountByHomeId(this._accountId)

      console.log('AuthService: Login request config:', {
        scopes: this._loginRequest.scopes,
        account: this._loginRequest.account?.homeAccountId,
      })

      this.msalInstance
        .acquireTokenSilent(this._loginRequest)
        .then((resp) => {
          console.log('AuthService: ✓ acquireTokenSilent success', resp)
          if (resp.account) {
            console.log('AuthService: Loading token from silent acquisition')
            this.loadToken(resp)
          } else {
            console.warn('AuthService: ⚠ Silent token acquired but no account in response')
          }
        })
        .catch((error) => {
          console.error('AuthService: ✗ acquireTokenSilent error:', error)
          console.error('AuthService: Silent token error details:', {
            errorCode: error.errorCode,
            errorMessage: error.errorMessage,
            subError: error.subError,
          })
          this.handleFinishedNoAuth()
        })
    } else {
      console.log('AuthService: No existing accounts found, finishing without auth')
      this.handleFinishedNoAuth()
    }
  }

  clearBrowserStorage () {
    this.msalInstance.clearCache()
  }

  handleFinishedNoAuth () {
    console.log('AuthService: handleFinishedNoAuth - User NOT authenticated')
    console.log('AuthService: Final state:', {
      hasAccessToken: !!this._accessToken,
      hasAccountId: !!this._accountId,
      hasLoyaltyId: this._loyaltyId > 0,
      isAuthenticated: this.isAuthenticated,
      isMember: this.isMember,
      loginHint: this._loginHint,
    })
    this._isFinished = true
    this.emit('ended', {})
  }

  loadToken (resp) {
    console.log('AuthService: loadToken called', resp)

    if (!resp.accessToken || !resp.account) {
      console.warn('AuthService: ⚠ loadToken - Missing accessToken or account', {
        hasAccessToken: !!resp.accessToken,
        hasAccount: !!resp.account,
      })
      this._idTokenClaims = ''
      this._accountId = ''
      this._loginHint = ''
      this._accessToken = ''
      return
    }

    console.log('AuthService: ✓ Setting token data', {
      accountId: resp.account.homeAccountId,
      hasAccessToken: !!resp.accessToken,
      tokenLength: resp.accessToken?.length,
      scopes: resp.scopes,
    })

    this._idTokenClaims = resp.idTokenClaims
    this._accountId = resp.account.homeAccountId
    this._loginHint = resp.account.idTokenClaims.login_hint
    this._accessToken = resp.accessToken

    if (typeof aa !== 'undefined' && this._accountId) {
      aa('setUserToken', 'maistra-' + this._accountId.substring(0, 36))
    }

    console.log('AuthService: Fetching user data from loyalty API')
    this.fetchAndSaveUserData()
  }

  fetchAndSaveUserData () {
    console.log('AuthService: fetchAndSaveUserData - Making API call to loyalty profiles')
    console.log('AuthService: API endpoint:', `${LOYALTY_API}/api/v1/loyaltyprofiles/me`)
    console.log('AuthService: Has access token:', !!this._accessToken)

    fetchWithTokenAsync(`${LOYALTY_API}/api/v1/loyaltyprofiles/me`, this._accessToken, 'GET')
      .then((obj) => {
        console.log('AuthService: ✓ Loyalty API response:', obj)
        this._loyaltyId = parseInt(obj?.userContact?.loyaltyProfileId)
        this._memberProfile = obj?.userContact
        console.log('AuthService: Loyalty ID:', this._loyaltyId)
        console.log('AuthService: Member profile loaded:', !!this._memberProfile)
      })
      .then(() => {
        this._isFinished = true
        const result = {
          displayName: this.getDisplayName(),
          accountId: this._accountId,
          loginHint: this._loginHint,
          loyaltyId: this._loyaltyId,
          isMaiFamily: this._memberProfile?.isMaiFamily,
        }
        console.log('AuthService: ✓✓✓ Authentication complete', result)
        this.emit('authenticated', result)
        this.emit('ended', {})
      })
      .catch((e) => {
        // Uncomment this to ignore Beterna CORS errors and treat user as logged in
        // const result = {
        //   displayName: this.getDisplayName(),
        //   accountId: this._accountId,
        //   loginHint: this._loginHint,
        //   loyaltyId: this._loyaltyId,
        // }
        // this.emit('authenticated', result)
        // this.emit('ended', {})
        console.error('AuthService: ✗ fetchAndSaveUserData ERROR:', e)
        console.error('AuthService: Error type:', e.constructor.name)
        if (e instanceof RestError) {
          console.error('AuthService: RestError details:', {
            status: e.status,
            message: e.message,
          })
          this.emit('error', e)
        } else if (e instanceof BeternaError) {
          console.error('AuthService: BeternaError details:', e)
          this.emit('error', e)
        } else {
          console.error('AuthService: Generic error, emitting AppError')
          this.emit('error', new AppError('me', ErrorLevel.FATAL))
        }
      })
  }

  async signIn () {
    await this._ensureInitialized()
    this.msalInstance.loginRedirect(this._loginRequest)
  }

  signOut () {
    this.emit('signingOut', {})
    const currentAccount = this.msalInstance.getAccountByHomeId(this._accountId)
    this.msalInstance.logout({
      account: currentAccount,
    })
  }

  get isAuthenticated () {
    return this._accessToken?.length > 5
  }

  get isMember () {
    return this.isAuthenticated && this._loyaltyId > 0
  }

  get isMaiFamily () {
    return this.isAuthenticated && this._memberProfile?.isMaiFamily
  }

  get accessToken () {
    return this._accessToken
  }

  get accountId () {
    return this._accountId
  }

  get loginHint () {
    return this._loginHint
  }

  getDisplayName () {
    if (this._memberProfile) {
      return `${this._memberProfile.firstName} ${this._memberProfile.lastName}`
    }
    if (!this._idTokenClaims) {
      return null
    }
    let result = ''
    if (this._idTokenClaims.given_name) {
      result += this._idTokenClaims.given_name + ' '
    }
    if (this._idTokenClaims.family_name) {
      result += this._idTokenClaims.family_name + ' '
    }
    return result
  }

  get isFinished () {
    return this._isFinished
  }

  get firstName () {
    return this._idTokenClaims?.given_name
  }

  get memberProfile () {
    return this._memberProfile
  }

  get lastName () {
    return this._idTokenClaims?.family_name
  }

  get email () {
    return this._idTokenClaims?.emails?.[0] || this._idTokenClaims?.email
  }

  get loyaltyId () {
    return this._loyaltyId
  }
}

// singleton
const AuthService = (() => {
  if (window.AuthService) {
    return window.AuthService
  }

  const instance = new _AuthService()
  window.AuthService = instance
  return instance
})()

export { fetchWithTokenAsync, AuthService }
export default AuthService
